import React, { useMemo } from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarsFilter, faCircleDashed } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import type { PartnerUser } from '@hooks/requests/queries/use_all_users_query';
import type { DateRangeValue } from '@ui';
import { OngoingCloseBadge, FilterLabelRow } from '@ui';
import { resolveCountryName } from '@constants/countries';
import { DashboardEmptyState } from '@components/dashboard';
import { getDateLabel, isOngoingPeriod } from './date_range_label';

// ── Types ──────────────────────────────────────────────────────────────────────

type UsersByCountryProps = {
  users: PartnerUser[];
  /** Omit for Partner roles — a single-org caller has no "which partner" to show. */
  orgLabel?: string;
  companyLabel?: string;
  dateRange?: DateRangeValue | null;
};

type CountryEntry = {
  name: string;
  count: number;
};

// ── Color palette ─────────────────────────────────────────────────────────────
// DS tokens (prototype starts brand-tone → teal-300), so slices follow the
// light/dark token remap like the rest of the card.

const CHART_COLORS = [
  'var(--au-brand-tone)',
  'var(--au-teal-300)',
  'var(--au-violet-300)',
  'var(--au-feedback-warning)',
  'var(--au-green-500)',
  'var(--au-violet-500)',
  'var(--au-bluegray-600)',
  'var(--au-red-200)',
];

// Donut geometry (prototype `.pie-wrap`: 152px, 8/42 ring thickness).
const DONUT_SIZE = 152;
const DONUT_CENTER = DONUT_SIZE / 2;

const DonutCenterLabel = ({ cx, cy, total }: { cx: number; cy: number; total: number }) => (
  <g>
    <text
      x={cx}
      y={cy - 7}
      textAnchor="middle"
      dominantBaseline="middle"
      className="ubc-donut-center-count"
      data-testid="donut-total"
    >
      {total.toLocaleString()}
    </text>
    <text
      x={cx}
      y={cy + 15}
      textAnchor="middle"
      dominantBaseline="middle"
      className="ubc-donut-center-label"
    >
      Total users
    </text>
  </g>
);

// ── Component ──────────────────────────────────────────────────────────────────

const UsersByCountry: React.FC<UsersByCountryProps> = ({
  users,
  orgLabel,
  companyLabel,
  dateRange,
}) => {
  const dateLabel = getDateLabel(dateRange);
  const isOngoing = isOngoingPeriod(dateRange);

  const countryData = useMemo((): CountryEntry[] => {
    const map = new Map<string, number>();
    users.forEach((u) => {
      const raw = u.country?.trim();
      const country =
        !raw || raw.toLowerCase() === 'unknown' ? 'Not specified' : resolveCountryName(raw);
      map.set(country, (map.get(country) ?? 0) + 1);
    });
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [users]);

  const total = users.length;
  // Exclude "Not specified" from the countries count
  const countryCount = countryData.filter((e) => e.name !== 'Not specified').length;

  // Assign colors by index (stable, not random per render)
  const colorFor = (i: number) => CHART_COLORS[i % CHART_COLORS.length];

  // Legend layout: 1–5 → single column, 6+ → two columns (Z-pattern)
  const twoColumns = countryCount >= 6;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="ubc-card">
      {/* Header */}
      <div className="ubc-header">
        <p className="ubc-eyebrow">Users by country</p>

        <div className="suc-filter-row">
          <FilterLabelRow orgLabel={orgLabel} companyLabel={companyLabel} dateLabel={dateLabel} />
          <OngoingCloseBadge isOngoing={isOngoing} />
        </div>
      </div>

      {total === 0 ? (
        <DashboardEmptyState />
      ) : (
        <div className="ubc-body">
          {/* Donut chart */}
          <div className="ubc-chart-wrap">
            <PieChart
              width={DONUT_SIZE}
              height={DONUT_SIZE}
              // Recharts' default 5px margin shifts the ring (not the centre
              // label) and clips it on the right/bottom — keep both on one point.
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            >
              <Pie
                data={countryData}
                cx={DONUT_CENTER}
                cy={DONUT_CENTER}
                innerRadius={43}
                outerRadius={72}
                startAngle={90}
                endAngle={-270}
                dataKey="count"
                strokeWidth={0}
                stroke="var(--ubc-stroke)"
                labelLine={false}
                label={false}
              >
                {countryData.map((_, i) => (
                  <Cell key={i} fill={colorFor(i)} />
                ))}
              </Pie>
              <DonutCenterLabel cx={DONUT_CENTER} cy={DONUT_CENTER} total={total} />
            </PieChart>
          </div>

          {/* Legend */}
          <div className="ubc-legend-wrap">
            <p className="ubc-country-count">
              <strong>{countryCount}</strong> {countryCount === 1 ? 'country' : 'countries'}
            </p>

            <ul className={`ubc-legend${twoColumns ? ' ubc-legend--two-col' : ''}`}>
              {countryData.map((entry, i) => {
                const pct = total > 0 ? Math.round((entry.count / total) * 100) : 0;
                return (
                  <li key={entry.name} className="ubc-legend-item">
                    <span className="ubc-legend-dot" style={{ background: colorFor(i) }} />
                    <span className="ubc-legend-name">{entry.name}</span>
                    <span className="ubc-legend-bull">·</span>
                    <span className="ubc-legend-count">
                      {entry.count.toLocaleString()} ({pct}%)
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersByCountry;
